import { useDebouncedValue, useElementSize, useIntersection } from '@mantine/hooks';
import { Divider } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Fade,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import SkeletonBox from 'components/generic/SkeletonBox';
import { PopperPropsExtended } from 'components/IconLabelFields/IconLabelTextField';
import {
  Fragment,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { isDeepEqual } from 'remeda';
import { bloxValues } from 'themes/constants';
import { hasLength } from 'utility/functions';
import { getCommonPopoutSx, getSolidThemeGray } from 'utility/styles';
import {
  dropdownInputAnchorOrigin,
  dropdownInputElevation,
  dropdownInputTransformOrigin,
  filterTextDebounceMs,
  fiveMinutesMs,
  getAccordionChipBoxSx,
  getAccordionChipSx,
  getCloseButtonSx,
  getDividerDecoratorSx,
  getDropdownInputThreshold,
  getExpandMoreIconSx,
  getFilterTextfieldSx,
  getFooterContainerSx,
  getFooterTypographySx,
  getFormControlSx,
  getHeaderLabelTypoSx,
  getInfoTextIconSx,
  getRemoveButtonSx,
  getSelectSx,
  getTooltipSx,
  InputChoice,
  maxPopoverHeightVh,
  maxPopoverWidthPx,
  tooltipPopperProps,
} from '../dropdownConsts';
import { SelectIconArrowSpinner } from './SelectIconArrowSpinner';
import { SelectMenuItem, SelectMenuItemProps } from './SelectMenuItem';
import { SelectNoResults } from './SelectNoResults';
import { RenderValueElementProps, SelectRenderValueElem } from './SelectRenderValueElem';
import {
  choiceTextString,
  deduplicateInputChoices,
  getSelectPlaceholderString,
  getUnlabeledItemsByValue,
  normalizeBaseValues,
} from './selectUtils';

// original versions (split single/multi select)
// 0fb5b2405e10a081b4d5ff591089554311034d28

export type PossibleEvent =
  | SyntheticEvent
  | React.MouseEvent<HTMLLIElement, MouseEvent>
  | React.MouseEvent<HTMLButtonElement, MouseEvent>
  | React.MouseEvent<HTMLDivElement, MouseEvent>
  | React.MouseEvent<SVGSVGElement, MouseEvent>;

export type FetchFnParams = {
  filterTextDebounced: string;
  pageParam?: string | number;
};

/* when using the fetchDatFn, format the API response to this expected shape */
export type FetchedFormattedInputChoices = {
  total?: number | string;
  next?: string | number | null;
  prev?: string | number | null;
  items?: InputChoice[];
};

export type SelectDropdownInputProps = {
  group?: string;
  value?: string | string[];
  /** Optional test ID to create test IDs in the component (component generates: component-name--testId) */
  testId?: string;
  /** `default: []`
   * additional choices to include in the list (e.g. prefetched, hardcoded, etc.)
   * note choices are deduped by .value so if the API returns a choice with same value
   * then the API choice will be used over the static choice
   */
  staticChoices?: InputChoice[];
  prioritizeStaticChoices?: boolean;
  showDividerAfterStaticChoices?: boolean;
  /** `default: "Single Select" | "Multi Select"` label for the select input header */
  inputLabel?: string;
  /** `function response must be in the `FetchedFormattedInputChoices` format`
   * provide a func to fetch data from the server - define it and pass it to the component
   * note: we will send up the filter text for your function definition, along with page and limit
   * this is useful when a typeahead query  'title' or 'type' or something other than just 'query',
   * or adding in other api fields e.g. "is_active", etc to the data fetch func call
   * since you can define those as params of the function you pass to this prop **/
  fetchDataFn?: (params: FetchFnParams) => Promise<FetchedFormattedInputChoices>;
  /** Optional function to fetch unlabeled items (items in selected values but not in fetched/static data)
   * Takes an array of UUIDs and returns formatted choices for those items
   * Used to fetch data for selected items that weren't in the initial paginated fetch
   */
  fetchUnlabeledItemsFn?: (uuids: string[]) => Promise<FetchedFormattedInputChoices>;
  /** Optional function to get cached items by UUIDs (synchronous)
   * If provided, will check cache first before marking items as loading
   * Useful to skip loading state when items were just selected from search results
   */
  getCachedItemsFn?: (uuids: string[]) => InputChoice[];
  /** override default query options  */
  queryOptions?: Partial<UseInfiniteQueryOptions<any, any, any, any, any>>;
  /** `default: true` infinite scroll enabled or disabled for fetching more data? */
  infiniteScroll?: boolean;
  /** `default: true` show the filter input box? */
  showFilter?: boolean;
  /** `default: Filter options...` placeholder text for the filter input box */
  filterPlaceholderText?: string;
  /** `default: No results found...` message to show when no results are found */
  noResultsMessage?: string;
  /** `default: true` show the total results and loading spinner in the footer? */
  showFooter?: boolean;
  /** `default: true` show the close button in the footer? */
  showCloseButton?: boolean;
  /** `default: true` show the chip box with selected values? */
  showChipBox?: boolean;
  /** add sx props for the form control (container) */
  formControlSx?: SxProps<Theme>;
  /** Optional Select sx overrides */
  selectSx?: SxProps<Theme>;
  /** add sx props for the popover (modal)  */
  popoverPaperSx?: SxProps<Theme>;
  /** `default: false` if true, we attach a ref to the form control and match the popover width to it  */
  popoverMatchesFormControlWidth?: boolean;
  /** `default: label` renderValueDisplay - label is the default e.g. [(3) sections] - chips - chip for each selection */
  renderValueDisplay?: 'label' | 'chips';
  /** `default: true if RVD == label, false if RVD == chips` renderValueTooltip - select has a tooltip showing selection labels */
  renderValueTooltip?: boolean;
  /**`default: 'inputLabel'`
   *  placeholder text for input - fallback to inputLabel if not provided
   * `stringify-options` will render the options for select box empty placeholder - truncated with ellipsis
   * `null` will render nothing, a blank box.
   */
  inputPlaceholderText?: 'stringify-options' | null | string;
  /** `default: standard` standard or outlined variant*/
  formControlVariant?: 'standard' | 'outlined';
  /** default: false - if true, displays a MUI InputLabel on the FormControl with shrink behavior */
  showFormControlLabel?: boolean;
  /** `default: false` - tell the component to use internal state for staging changes
   *  and only "apply" them (calls updateValue with new values) when the popover closes
   *  useful for the multiselect + conditional render & lower api call count vs constant updates per change
   **/
  applyChangesOnPopoverClose?: boolean;
  /** disabled - select is disabled and popover can't open */
  disabled?: boolean;
  /** loading - optionally control loading state */
  loading?: boolean;
  /** hidden - select field is hidden via browser hidden attr, so should stay mounted, also adds hidden prop to input */
  hidden?: boolean;
  /** If provided a truthy string, a info icon with tooltip hover text will appear to the right of the inputLabel */
  infoText?: string;
  /** if true will attempt to apply masking to areas for logrocket */
  isDataPrivate?: boolean;
  multiple?: boolean;
  /** pass a func to update the value of the select input (on-event callback) */
  updateValue: (event: PossibleEvent | any, value: string | string[]) => void;
  clearSelection?: (event: PossibleEvent) => void;
  renderValueElm?: React.ComponentType<RenderValueElementProps>;
  renderMenuItem?: React.ComponentType<SelectMenuItemProps>;
  /** `default: true` - close the popover on selection? but only for single selection dropdowns*/
  closePopverOnSelection?: boolean;
  /** `default: false` - show the remove button */
  showRemove?: boolean;
  /** `default: Remove` - text for the remove button */
  removeButtonText?: string;
  enableCycleSelection?: boolean; // if true, will cycle through choices on click add, remove, clear
  /** `default: true` - show unlabeled items (raw IDs) when selected values don't have corresponding labels in fetched/static data */
  showUnlabeledItems?: boolean;
  /** optional batch edit contextual interaction for cycle selection */
  addRemoveValues?: Record<string, any>;
  updateAddRemoveValue?: (
    group: string,
    value: string,
    action: 'add' | 'remove' | null,
  ) => void;
  /** `default: false` - allow users to create new items by typing in filter or render value area */
  allowCreateNew?: boolean;
  /** callback fired when a new item is created (optional - used for external tracking) */
  onCreateNewItem?: (item: InputChoice) => void;
  /** callback fired when a choice is selected, passing the full choice data for caching purposes */
  onChoiceSelected?: (choice: InputChoice) => void;
};

// Sensible default react-query options applied to every dropdown query.
// These are merged UNDER any consumer-provided queryOptions (see below) so that
// passing a partial override (e.g. just a custom queryKey from AddRemove.tsx batch
// edit) does not clobber them. See CMSADMIN-2530.
//
// Intentionally NOT using placeholderData: (prev) => prev. In RQ v5 that identity
// placeholder forces the query to report "success" even while a new fetch is
// pending, which hides loading/error status from consumers in non-obvious ways.
// staleTime already de-dupes identical queries, so cache hits render instantly
// without it.
const DEFAULT_QUERY_OPTIONS = {
  staleTime: fiveMinutesMs,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

export const SelectDropdownInput = memo(
  ({
    group = '',
    staticChoices = [],
    prioritizeStaticChoices = false,
    showDividerAfterStaticChoices = false,
    inputLabel = 'Select',
    inputPlaceholderText = inputLabel,
    value = [],
    updateValue,
    clearSelection,
    infiniteScroll = true,
    fetchDataFn,
    testId,
    queryOptions = {},
    showFilter = true,
    filterPlaceholderText,
    noResultsMessage,
    showFooter = true,
    showCloseButton = true,
    showChipBox = true,
    formControlSx,
    selectSx,
    popoverPaperSx,
    popoverMatchesFormControlWidth = false,
    renderValueDisplay = 'label',
    renderValueTooltip,
    infoText = '',
    formControlVariant = 'standard',
    showFormControlLabel = false,
    disabled = false,
    loading = false,
    hidden = false,
    isDataPrivate = false,
    renderValueElm = undefined,
    renderMenuItem = undefined,
    applyChangesOnPopoverClose = false,
    multiple = false,
    closePopverOnSelection = true,
    showRemove = true,
    removeButtonText,
    enableCycleSelection = false,
    showUnlabeledItems = true,
    addRemoveValues,
    updateAddRemoveValue,
    allowCreateNew = false,
    onCreateNewItem,
    fetchUnlabeledItemsFn,
    getCachedItemsFn,
    onChoiceSelected,
  }: SelectDropdownInputProps) => {
    const { t } = useTranslation();

    // Store addition of new custom choices
    const [customChoices, setCustomChoices] = useState<InputChoice[]>([]);

    // Cycle selection state
    const cycleSelectionState = useMemo(() => {
      const state: Record<string, 'add' | 'remove'> = {};
      if (!enableCycleSelection || !group || !addRemoveValues) return state;
      addRemoveValues[group]?.forEach((item: any) => {
        const action = item.misc?.action;
        if (action === 'add' || action === 'remove') {
          state[item.value] = action;
        }
      });
      return state;
    }, [addRemoveValues, enableCycleSelection, group]);

    // Data vars / hooks
    const [filterText, setFilterText] = useState('');

    // UI vars / hooks
    const [popoverOpen, setPopoverOpen] = useState(false);

    // Setup consts / state for applyChangesOnPopoverClose prop
    const emptyValue: string | string[] = multiple ? [] : '';
    const [stagedValues, setStagedValues] = useState<string | string[]>(emptyValue);

    // Determine the current values to use for the select - via passed value or local state copy?
    const baseValues = applyChangesOnPopoverClose ? stagedValues : value;

    // Use normalizeBaseValues utility for consistent normalization
    const currentValues = useMemo(() => {
      const normalizedBase = normalizeBaseValues(baseValues);

      if (!enableCycleSelection || !group) return normalizedBase;

      const removeCycleValues = Object.entries(cycleSelectionState)
        .filter(([, state]) => state === 'remove')
        .map(([value]) => value);

      const combined = new Set([...normalizedBase, ...removeCycleValues]);
      return Array.from(combined);
    }, [cycleSelectionState, enableCycleSelection, group, baseValues]);

    const theme = useTheme();
    // Debounce the raw filter text, then trim leading/trailing whitespace before it
    // drives the query key, the fetch param, and client-side filtering. Pasted values
    // (emails, names copied from other apps) frequently carry trailing spaces or
    // newlines that otherwise produce inconsistent "no results" searches. Internal
    // whitespace (e.g. "Sean Tripp") is preserved; the visible input keeps filterText.
    const [rawDebouncedFilterText] = useDebouncedValue(filterText, filterTextDebounceMs);
    const debouncedFilterText = rawDebouncedFilterText.trim();
    const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });

    // Reference to the TextField for manual focus control
    const filterInputRef = useRef<HTMLInputElement | null>(null);

    const popoverAnchorRef = useRef<HTMLElement | null>(null);
    const { ref: elSizeRef, width: formCtrlWidth } = useElementSize();

    // Sync values passed in to internal state...
    useEffect(() => {
      // Don't update local state if there's nothing to update
      if (isDeepEqual(value, stagedValues)) {
        return;
      }
      // Don't update local state if the popover is open
      // and we're in applyChangesOnPopoverClose mode
      if (popoverOpen && applyChangesOnPopoverClose) {
        return;
      }

      setStagedValues(value); // If we pass the guards, update the local state
    }, [stagedValues, setStagedValues, popoverOpen, applyChangesOnPopoverClose, value]);

    // From SelectDropdownInput
    // Manually focus the TextField when the Popover is open
    useEffect(() => {
      if (popoverOpen && filterInputRef.current) {
        filterInputRef.current.focus();
      }
    }, [popoverOpen]); // Runs whenever popoverOpen changes

    // Store data the API Returns for choices - this is since the fetchedData may not necessarily have all the choices on a typeahead filter
    const [allChoicesDeduped, setAllChoicesDeduped] = useState<InputChoice[]>([]);

    // Store unlabeled items that were successfully fetched from the API
    const [fetchedUnlabeledItems, setFetchedUnlabeledItems] = useState<InputChoice[]>([]);

    // inputLabel is used for the queryKey prefix in most cases
    // this is assuming the data is unique to the inputLabel...
    // but in some cases this can cause a cache collision
    // eg: multiple dropdowns use the same inputLabel
    // but have different underlying fetchDataFn's..
    // eg: see AddRemove.tsx batch-edit workflows grouping
    // since we dont want incorrect query cache data used
    // can pass custom queryKey override via queryOptions.
    // Destructure queryKey out of queryOptions so the spread below cannot override
    // the computed queryKey — which must always include debouncedFilterText so that
    // React Query refetches whenever the filter changes.
    const { queryKey: _queryKeyOption, ...restQueryOptions } = queryOptions ?? {};

    // Build a flat base array. If a custom queryKey was provided (e.g. [group, 'batch-select-input'])
    // keep it as the prefix; otherwise derive one from group and inputLabel.
    // debouncedFilterText is appended below so the key always changes on filter input.
    const queryKeyBase: unknown[] = useMemo(() => {
      if (_queryKeyOption) {
        return Array.isArray(_queryKeyOption) ? _queryKeyOption : [_queryKeyOption];
      }
      return [`${group}-${inputLabel}-select-input`];
    }, [_queryKeyOption, group, inputLabel]);

    // Setup infinity query
    const {
      data: fetchedData,
      fetchNextPage,
      hasNextPage,
      isFetching,
      isLoading: queryIsLoading,
      refetch,
      error,
    } = useInfiniteQuery<
      FetchedFormattedInputChoices,
      Error,
      InfiniteData<FetchedFormattedInputChoices, string>,
      any[],
      string | number
    >({
      // Defaults first so consumer queryOptions (restQueryOptions) win on conflict,
      // while a partial override only changes what it explicitly sets.
      ...DEFAULT_QUERY_OPTIONS,
      queryKey: [...queryKeyBase, debouncedFilterText],
      queryFn: async ({ pageParam }) => {
        try {
          if (!hasLength(staticChoices) && !fetchDataFn) {
            throw new Error(`Please provide a fetchDataFn or/and static choices!`);
          }
          if (fetchDataFn) {
            // pass up certain local state to the parent fetch function
            // sort of like an event bubbling up
            const sendingParamsUp: FetchFnParams = {
              filterTextDebounced: debouncedFilterText,
              pageParam: pageParam as string,
            };
            const result = await fetchDataFn(sendingParamsUp);
            return result; // return the response to useQuery
          }
          // If no fetchDataFn, return empty response immediately
          return { items: [], next: undefined, prev: undefined };
        } catch (error) {
          throw new Error(`[${inputLabel}-SelectDropdownInput] useQuery error: ${error}`);
        }
      },
      // Only auto-enable if there's a fetchDataFn, allow queryOptions to override
      enabled: !!fetchDataFn,
      initialPageParam: '',
      getNextPageParam: (response: FetchedFormattedInputChoices) => {
        return response?.next;
      },
      getPreviousPageParam: (response: FetchedFormattedInputChoices) => {
        return response?.prev;
      },
      ...restQueryOptions,
    });

    // Query loading or props loading is true
    const isLoading = queryIsLoading || loading;

    // Flatten infinite query data
    const flatApiChoices: InputChoice[] = useMemo(() => {
      if (!fetchedData?.pages) return [];
      const result: InputChoice[] = [];
      // loop thru pages, check if valid obj, and push to result
      for (const page of fetchedData.pages) {
        const items = (page as FetchedFormattedInputChoices)?.items || [];
        for (const option of items) {
          if (
            option &&
            option.hasOwnProperty('value') &&
            option.hasOwnProperty('label')
          ) {
            result.push(option);
          }
        }
      }

      return result;
    }, [fetchedData]);

    // Calculate which UUIDs need fetching based on current state
    // Memoized so the effect only re-runs when this set actually changes
    const uuidsNeedingFetch = useMemo(() => {
      if (!fetchUnlabeledItemsFn || !showUnlabeledItems) {
        return [];
      }

      const selectedValues = Array.isArray(value) ? value : [value];

      if (!selectedValues.length) {
        return [];
      }

      const unlabeledItems = getUnlabeledItemsByValue(
        staticChoices,
        [...flatApiChoices, ...fetchedUnlabeledItems],
        selectedValues,
      );

      return unlabeledItems.map((item) => String(item.value));
    }, [
      value,
      staticChoices,
      flatApiChoices,
      fetchedUnlabeledItems,
      fetchUnlabeledItemsFn,
      showUnlabeledItems,
    ]);

    // Fetch unlabeled items if a fetch function is provided
    useEffect(() => {
      if (uuidsNeedingFetch.length === 0) {
        return;
      }

      // Check cache first if getCachedItemsFn is provided
      let uuidsToFetch = uuidsNeedingFetch;

      if (getCachedItemsFn) {
        const cachedItems = getCachedItemsFn(uuidsNeedingFetch);
        if (cachedItems.length > 0) {
          const cachedIds = new Set(cachedItems.map((item) => String(item.value)));
          setFetchedUnlabeledItems((prev) => {
            const existingValues = new Set(prev.map((p) => String(p.value)));
            const newItems = cachedItems.filter(
              (item) => !existingValues.has(String(item.value)),
            );
            return newItems.length > 0 ? [...prev, ...newItems] : prev;
          });
          uuidsToFetch = uuidsNeedingFetch.filter((uuid) => !cachedIds.has(uuid));
        }
      }

      // Fetch any UUIDs not satisfied by cache
      const fetchFn = fetchUnlabeledItemsFn;
      if (uuidsToFetch.length > 0 && fetchFn) {
        fetchFn(uuidsToFetch)
          .then((result) => {
            const fetchedItems: InputChoice[] = result.items ?? [];
            if (fetchedItems.length > 0) {
              setFetchedUnlabeledItems((prev) => {
                const existingValues = new Set(prev.map((p) => String(p.value)));
                const newItems = fetchedItems.filter(
                  (item) => !existingValues.has(String(item.value)),
                );
                return newItems.length > 0 ? [...prev, ...newItems] : prev;
              });
            }
          })
          .catch((error) => {
            console.error(
              `[${inputLabel}-SelectDropdownInput] Failed to fetch unlabeled items:`,
              error,
            );
          });
      }
    }, [uuidsNeedingFetch, fetchUnlabeledItemsFn, getCachedItemsFn, inputLabel]);

    // Update allChoicesDeduped state if the newest deduped choices change
    useEffect(() => {
      setAllChoicesDeduped((prev) => {
        // normalize selected value(s) as an array
        const selectedValues = Array.isArray(value) ? value : [value];
        // determine choice items that are unlabeled
        // eg: val exists in qp, may not be fetched in list yet..
        // Note: we check against both flatApiChoices and fetchedUnlabeledItems
        const unlabeledItems = showUnlabeledItems
          ? getUnlabeledItemsByValue(
              staticChoices,
              [...flatApiChoices, ...fetchedUnlabeledItems],
              selectedValues,
            )
          : [];
        // deduplicate all input choices
        // Include fetchedUnlabeledItems in the fetchedData so they're used instead of placeholder UUIDs
        const newDedupedItems = deduplicateInputChoices({
          staticChoices: [...staticChoices, ...customChoices],
          fetchedData: [...flatApiChoices, ...fetchedUnlabeledItems],
          possiblyUnknownItems: unlabeledItems,
          prioritizeStaticChoices: prioritizeStaticChoices,
        });
        // determine if state needs to be updated
        const needsUpdate = !isDeepEqual(newDedupedItems, prev);
        // return the new deduped or prev if update needed
        return needsUpdate ? newDedupedItems : prev;
      });
    }, [
      value,
      staticChoices,
      flatApiChoices,
      fetchedUnlabeledItems,
      prioritizeStaticChoices,
      customChoices,
      showUnlabeledItems,
    ]);

    // Filtering: always include headers in dedupedChoices, but only show them if their group has a matching child
    function matchesFilter(choice: InputChoice) {
      // Always include headers for grouping
      if (choice.__isHeader) {
        return true;
      }
      const text = debouncedFilterText.toLowerCase();
      return [
        choice.label,
        choice.sublabel,
        choice.description,
        choice.value && String(choice.value),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(text));
    }
    // Filter all sources, but always include headers when applicable
    const filteredApiChoices = flatApiChoices.filter(matchesFilter);
    const filteredStaticChoices = staticChoices.filter(matchesFilter);
    const filteredCustomChoices = customChoices.filter(matchesFilter);
    const filteredUnlabeledItems = (
      showUnlabeledItems
        ? getUnlabeledItemsByValue(
            staticChoices,
            [...flatApiChoices, ...fetchedUnlabeledItems],
            Array.isArray(value) ? value : [value],
          )
        : []
    ).filter(matchesFilter);
    // Merge and dedupe
    const mergedChoices = [
      ...filteredCustomChoices,
      ...filteredApiChoices,
      ...filteredStaticChoices,
      ...filteredUnlabeledItems,
    ];
    const seenVals = new Set();
    const dedupedChoices = mergedChoices.filter((item) => {
      const val = String(item.value);
      if (seenVals.has(val)) {
        return false;
      }
      seenVals.add(val);
      return true;
    });

    // Header logic: include header only if at least one child matches
    function filterHeadersWithChildren(choices: InputChoice[]): InputChoice[] {
      // Group all dedupedChoices into {header, children[]} for reference
      const groups: { header: InputChoice | null; children: InputChoice[] }[] = [];
      let currentHeader: InputChoice | null = null;
      let currentChildren: InputChoice[] = [];
      for (const item of dedupedChoices) {
        if (item.__isHeader) {
          if (currentHeader) {
            groups.push({ header: currentHeader, children: currentChildren });
          } else if (currentChildren.length > 0) {
            groups.push({ header: null, children: currentChildren });
          }
          currentHeader = item;
          currentChildren = [];
        } else {
          currentChildren.push(item);
        }
      }
      if (currentHeader) {
        groups.push({ header: currentHeader, children: currentChildren });
      } else if (currentChildren.length > 0) {
        groups.push({ header: null, children: currentChildren });
      }

      // If not filtering, flatten groups (header, children) for display, no duplicates
      if (!debouncedFilterText) {
        const result: InputChoice[] = [];
        for (const group of groups) {
          if (group.header) result.push(group.header);
          result.push(...group.children);
        }
        return result;
      }

      // Filtering: for each group, if any child matches, show header and matching children
      const filteredSet = new Set(choices.map((c) => c.value));
      const result: InputChoice[] = [];
      for (const group of groups) {
        const matchingChildren = group.children.filter((c) => filteredSet.has(c.value));
        if (group.header && matchingChildren.length > 0) {
          result.push(group.header);
        }
        result.push(...matchingChildren);
      }
      return result;
    }
    const filteredUniqueChoices = filterHeadersWithChildren(dedupedChoices);

    const countFilteredUniqueChoices = filteredUniqueChoices.filter(
      (item: InputChoice) => !String(item.value).startsWith('__header__'),
    ).length;

    // Deduplicate API items
    const apiItemSet = new Set(flatApiChoices.map((item: InputChoice) => item.value));

    // Client side additional items - may not be in the API response (e.g. filtered staticChoices)
    const uniqueClientSideItems = filteredUniqueChoices.filter(
      (item) => !apiItemSet.has((item as InputChoice).value),
    );

    // Total Results Calculation
    // Use API total when available, but subtract known headers from loaded data
    const apiTotal = useMemo(() => {
      if (!fetchedData?.pages?.length) {
        return undefined;
      }
      const lastPage = fetchedData.pages[fetchedData.pages.length - 1];
      const raw = (lastPage as FetchedFormattedInputChoices)?.total;
      return raw != null ? Number(raw) : undefined;
    }, [fetchedData]);

    const loadedHeaderCount = flatApiChoices.filter((item: InputChoice) =>
      String(item.value).startsWith('__header__'),
    ).length;

    const clientSideOnlyCount = uniqueClientSideItems.filter(
      (item) => !String((item as InputChoice).value).startsWith('__header__'),
    ).length;

    const totalResults =
      apiTotal != null
        ? apiTotal - loadedHeaderCount + clientSideOnlyCount
        : flatApiChoices.filter(
            (item: InputChoice) => !String(item.value).startsWith('__header__'),
          ).length + clientSideOnlyCount;

    // Determine which items are selected
    const selectedItems = allChoicesDeduped.filter((choice: InputChoice) =>
      currentValues.includes(choice.value),
    );

    // Determine render value placeholder text
    const selectPlaceholderText = getSelectPlaceholderString(
      inputPlaceholderText,
      allChoicesDeduped,
      inputLabel,
    );

    // Tooltip logic
    const showTooltip = renderValueTooltip || renderValueDisplay === 'label';
    const tooltipText = showTooltip
      ? selectedItems.map((choice) => choiceTextString(choice)).join('\n')
      : '';
    const tooltipTitle = !popoverOpen ? tooltipText : ''; // don't show if popup open

    // Consts loading states / initial load / etc... (no existing data and provided fetch function or via props disabled/hidden)
    const loadingWithoutData = !hasLength(allChoicesDeduped) && isLoading; // status loading -  no cached data and the query is currently fetching
    const isDisabled = disabled || loadingWithoutData;
    const isHidden = hidden;

    // Anything we need to show in the footer?
    const hasFooterItems = showFooter || showCloseButton;

    // Infinity Scroll Listener
    useEffect(() => {
      if (
        fetchDataFn &&
        entry?.isIntersecting &&
        hasNextPage &&
        !isFetching &&
        infiniteScroll
      ) {
        fetchNextPage();
      }
    }, [entry, hasNextPage, isFetching, fetchDataFn, fetchNextPage, infiniteScroll]);

    // Event handlers
    const handlePopoverOpen = () => {
      setPopoverOpen(true);
    };

    const handlePopoverClose = () => {
      setFilterText(''); // reset the filter text
      if (applyChangesOnPopoverClose) {
        updateValue(null, stagedValues); // call updateValue onClose of the popover
      }
      setPopoverOpen(false);
    };

    // Handle both single and multiple select cases (string / string[])
    const handleSelectionChange = (event: PossibleEvent, selectedValue: string) => {
      const normalizedSelectedValue = String(selectedValue);

      // Cache the selected choice data and preload into fetchedUnlabeledItems
      // Prevents a UUID flash: the data is already in allChoicesDeduped (from the open dropdown)
      // Push it into fetchedUnlabeledItems immediately so its
      // available on the next render, after the popover closes.
      const selectedChoice = allChoicesDeduped.find(
        (c) => c.value === normalizedSelectedValue,
      );
      if (selectedChoice) {
        if (onChoiceSelected) {
          onChoiceSelected(selectedChoice);
        }
        setFetchedUnlabeledItems((prev) => {
          const existingValues = new Set(prev.map((p) => String(p.value)));
          if (existingValues.has(String(selectedChoice.value))) {
            return prev;
          }
          return [...prev, selectedChoice];
        });
      }

      if (multiple) {
        // multi: add string into array if not present, remove if present
        const currentArray = Array.isArray(currentValues) ? currentValues : [];
        const isSelected = currentArray.includes(normalizedSelectedValue);
        const currentCycle = cycleSelectionState[normalizedSelectedValue];

        let newValues: string[];

        if (enableCycleSelection && group && updateAddRemoveValue) {
          if (!currentCycle) {
            // 1st click: add
            newValues = [...currentArray, normalizedSelectedValue];
            updateAddRemoveValue(group, normalizedSelectedValue, 'add');
          } else if (currentCycle === 'add') {
            // 2nd click: mark for removal
            newValues = [...currentArray, normalizedSelectedValue];
            updateAddRemoveValue(group, normalizedSelectedValue, 'remove');
          } else if (currentCycle === 'remove') {
            // 3rd click: clear selection
            newValues = currentArray.filter((v) => v !== normalizedSelectedValue);
            updateAddRemoveValue(group, normalizedSelectedValue, null);
          } else {
            // No change fallback
            newValues = [...currentArray];
          }
        } else {
          const isExclusive = allChoicesDeduped.find(
            (c) => c.value === normalizedSelectedValue,
          )?.exclusive;

          if (isExclusive) {
            // Toggle off exclusive if already selected
            newValues = isSelected ? [] : [normalizedSelectedValue];
          } else {
            const exclusiveAlreadySelected = currentArray.some(
              (val) => allChoicesDeduped.find((c) => c.value === val)?.exclusive,
            );

            if (exclusiveAlreadySelected) {
              // Replace exclusive with this non-exclusive option
              newValues = [normalizedSelectedValue];
            } else {
              newValues = isSelected
                ? currentArray.filter((v) => v !== normalizedSelectedValue)
                : [...currentArray, normalizedSelectedValue];
            }
          }
        }

        // If applyChangesOnPopoverClose is true, stage the values
        if (applyChangesOnPopoverClose) {
          setStagedValues(newValues);
        } else {
          updateValue(event, newValues);
        }

        return;
      } else {
        // single: string toggles select or maintains if already selected
        if (applyChangesOnPopoverClose) {
          setStagedValues(normalizedSelectedValue);
        } else {
          updateValue(event, normalizedSelectedValue);
        }

        if (closePopverOnSelection) {
          handlePopoverClose();
        }
      }
    };

    const handleClearSelection = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      if (applyChangesOnPopoverClose) {
        if (clearSelection) {
          clearSelection(event);
          return;
        }
        setStagedValues(emptyValue);
      } else {
        if (clearSelection) {
          clearSelection(event);
          return;
        }
        updateValue(event, emptyValue);
      }
    };

    const handleDeleteSelection = (event: PossibleEvent, valueToDelete?: string) => {
      // multi select: find and remove from current values array
      if (multiple) {
        const currentArray = Array.isArray(currentValues) ? currentValues : [];
        const newValues = currentArray.filter((v) => v !== valueToDelete);
        if (applyChangesOnPopoverClose) {
          setStagedValues(newValues);
        } else {
          updateValue(event, newValues);
        }
      } else {
        // single select: can just clear it
        if (applyChangesOnPopoverClose) {
          setStagedValues('');
        } else {
          updateValue(event, '');
        }
      }
    };

    const handleFilterChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setFilterText(event.target.value);
    };

    const handleClearFilter = () => {
      setFilterText('');
    };

    // Add a new handler for renderValueElm clicks
    const handleSelectClick = (event: React.MouseEvent) => {
      // if disabled or hidden do not open the popover
      if (disabled || isHidden) {
        return;
      }

      // Stop immediate propagation to prevent multiple handlers from firing
      event.stopPropagation();
      // Get the original target element
      const target = event.target as HTMLElement;
      // Check if the click was on a Chip or its delete icon
      const isChipDelete = target.closest('.MuiChip-deleteIcon');
      // If it's a Chip's delete icon, don't do anything (let the Chip's onDelete handle it)
      if (isChipDelete) {
        return;
      }
      // for any other click, open the popover
      handlePopoverOpen();
    };

    // Handle keydown events for the filter input (supports creating new items)
    const handleKeyDownFilterInput = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const canCreateNew = enableCycleSelection || allowCreateNew;
      if (!canCreateNew) return;

      const input = e.target as HTMLInputElement;
      const trimmedText = input.value.trim();

      // Support both Enter and Tab keys for adding new items
      if ((e.key === 'Enter' || e.key === 'Tab') && trimmedText) {
        const exists = allChoicesDeduped.some((c) => c.value === trimmedText);

        if (!exists) {
          const newItem: InputChoice = {
            value: trimmedText,
            label: trimmedText,
          };

          // Add to custom choices
          setCustomChoices((prev) => [...prev, newItem]);

          // Notify parent of new item creation
          onCreateNewItem?.(newItem);

          // Handle cycle selection mode (batch edit)
          if (enableCycleSelection && group && updateAddRemoveValue) {
            updateAddRemoveValue(group, newItem.value, 'add');
          } else {
            // Auto-select the new item when not in cycle selection mode
            if (multiple) {
              const currentArray = Array.isArray(currentValues) ? currentValues : [];
              const newValues = [...currentArray, trimmedText];
              if (applyChangesOnPopoverClose) {
                setStagedValues(newValues);
              } else {
                updateValue(e, newValues);
              }
            } else {
              if (applyChangesOnPopoverClose) {
                setStagedValues(trimmedText);
              } else {
                updateValue(e, trimmedText);
              }
            }
          }

          setFilterText('');
        }

        e.preventDefault();
      }
    };

    // Handle creating new item from render value input
    const handleCreateNewFromRenderValue = useCallback(
      (newValue: string) => {
        const trimmedValue = newValue.trim();
        if (!trimmedValue) return;

        const exists = allChoicesDeduped.some((c) => c.value === trimmedValue);
        if (exists) return;

        const newItem: InputChoice = {
          value: trimmedValue,
          label: trimmedValue,
        };

        // Add to custom choices
        setCustomChoices((prev) => [...prev, newItem]);

        // Notify parent of new item creation
        onCreateNewItem?.(newItem);

        // Auto-select the new item
        if (multiple) {
          const currentArray = Array.isArray(currentValues) ? currentValues : [];
          const newValues = [...currentArray, trimmedValue];
          if (applyChangesOnPopoverClose) {
            setStagedValues(newValues);
          } else {
            updateValue(null, newValues);
          }
        } else {
          if (applyChangesOnPopoverClose) {
            setStagedValues(trimmedValue);
          } else {
            updateValue(null, trimmedValue);
          }
        }
      },
      [
        allChoicesDeduped,
        applyChangesOnPopoverClose,
        currentValues,
        multiple,
        onCreateNewItem,
        updateValue,
      ],
    );

    // remove button disabled state
    // satisfy both array/string multi/single modes
    const valueIsEmpty: boolean = useMemo(() => {
      return currentValues.length === 0;
    }, [currentValues]);

    // showChipBox typography text
    // multi mode show num of selections
    // single mode just only set 1 or None
    const chipBoxText: string = useMemo(() => {
      if (!showChipBox) return '';
      // multi select mode
      if (Array.isArray(currentValues)) {
        return t('blox.general.count_selected', { count: currentValues.length });
      }
      // single select mode
      if (typeof currentValues === 'string') {
        return currentValues
          ? t('blox.general.count_selected', { count: 1 })
          : t('blox.general.count_selected', { count: 0 });
      }
      return '';
    }, [currentValues, showChipBox, t]);

    // chipbox chips needs value
    // as an an array to map over
    const chipBoxValueArr: string[] = useMemo(() => {
      if (!showChipBox) return [];
      return currentValues;
    }, [currentValues, showChipBox]);

    // Translations / Text
    const filterPlaceholder = useMemo(() => {
      const txt = filterPlaceholderText ?? `${t('blox.actions.filter_options')}...`;
      return txt;
    }, [filterPlaceholderText, t]);

    // Normalize ID for test IDs
    // Use testId prop if provided, otherwise use inputLabel, then 'select-dropdown-input' as fallback
    const BaseId = testId || inputLabel.trim() || 'select-dropdown-input';
    const normalizedInputId = BaseId.toLowerCase().replace(/\s+/g, '-');

    return (
      <Tooltip
        title={tooltipTitle}
        disableInteractive={true}
        disableFocusListener={true}
        slotProps={{
          popper: {
            'data-private': isDataPrivate ? true : undefined,
          } as PopperPropsExtended,
          tooltip: {
            sx: {
              ...getTooltipSx(theme),
            },
          },
        }}
      >
        <FormControl
          data-testid={`select-dropdown-form-control--${normalizedInputId}`}
          size="small"
          variant={formControlVariant}
          ref={elSizeRef}
          disabled={isDisabled}
          hidden={isHidden}
          sx={{
            ...getFormControlSx(theme, popoverOpen),
            ...(typeof formControlSx === 'function'
              ? formControlSx(theme)
              : formControlSx),
            ...(isHidden ? { display: 'none' } : {}),
          }}
        >
          {showFormControlLabel && <InputLabel shrink>{inputLabel}</InputLabel>}
          <Select
            ref={popoverAnchorRef}
            multiple={multiple}
            disabled={isDisabled}
            hidden={isHidden}
            id={`${normalizedInputId}-${multiple ? 'multi' : 'single'}-select-input`}
            data-testid={`${normalizedInputId}-${multiple ? 'multi' : 'single'}-select-input`}
            label={showFormControlLabel ? inputLabel : undefined}
            value={currentValues} // warning "out-of-range" - https://github.com/mui/material-ui/issues/18494  - see file notes EOF single select
            MenuProps={{ open: false }} // use custom popover
            open={popoverOpen}
            onClick={handleSelectClick}
            sx={{
              ...getSelectSx(theme),
              ...(typeof selectSx === 'function' ? selectSx(theme) : selectSx),
            }}
            displayEmpty={true}
            IconComponent={isLoading ? () => <SelectIconArrowSpinner /> : undefined}
            renderValue={() => {
              if (showFormControlLabel && valueIsEmpty) {
                return null;
              }
              if (renderValueElm) {
                const CustomRenderValueElm = renderValueElm;
                return (
                  <CustomRenderValueElm
                    group={group}
                    selectedChoices={selectedItems}
                    placeholderText={selectPlaceholderText}
                    isLoading={isLoading}
                    renderValueDisplay={renderValueDisplay}
                    isDataPrivate={isDataPrivate}
                    allowCreateNew={allowCreateNew}
                    onCreateNewItem={handleCreateNewFromRenderValue}
                    disabled={disabled}
                  />
                );
              }

              return (
                <SelectRenderValueElem
                  group={group}
                  selectedChoices={selectedItems}
                  placeholderText={selectPlaceholderText}
                  isLoading={isLoading}
                  renderValueDisplay={renderValueDisplay}
                  isDataPrivate={isDataPrivate}
                  allowCreateNew={allowCreateNew}
                  onCreateNewItem={handleCreateNewFromRenderValue}
                  disabled={disabled}
                />
              );
            }}
          >
            {/**
             * To suppress the out-of-range value warning: add the empty default & always have the current value as a choice.
             */}
            <MenuItem
              value={emptyValue}
              hidden={true}
              style={{ display: 'none' }}
              data-private={isDataPrivate ? true : undefined}
            />
            <MenuItem
              value={currentValues}
              hidden={true}
              style={{ display: 'none' }}
              data-private={isDataPrivate ? true : undefined}
            />
          </Select>

          <Popover
            data-testid={`select-dropdown-popover--${normalizedInputId}`}
            anchorEl={popoverAnchorRef.current}
            open={popoverOpen}
            hidden={isHidden}
            keepMounted={true}
            onClose={handlePopoverClose}
            anchorOrigin={dropdownInputAnchorOrigin}
            transformOrigin={dropdownInputTransformOrigin}
            elevation={dropdownInputElevation}
            marginThreshold={getDropdownInputThreshold(theme)}
            slotProps={{
              paper: {
                sx: {
                  display: 'grid',
                  gridTemplateRows: 'auto 1fr auto',
                  gridTemplateAreas: `'selectheader' 'selectmainarea' 'selectfooter'`,
                  minWidth: '200px',
                  container: 'selectgrid / normal',
                  overflow: 'hidden',
                  width: popoverMatchesFormControlWidth
                    ? formCtrlWidth
                    : maxPopoverWidthPx,
                  maxHeight: maxPopoverHeightVh,
                  ...getCommonPopoutSx(theme),
                  ...(typeof popoverPaperSx === 'function'
                    ? popoverPaperSx(theme)
                    : popoverPaperSx),
                },
              },
            }}
          >
            <Stack
              direction="column"
              sx={{
                gridArea: 'selectheader',
                zIndex: 1,
                background: getSolidThemeGray(theme),
                borderBottom: `1px solid ${theme.palette.action.selected}`,
                p: 1,
                gap: 1,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Tooltip
                  title={infoText}
                  arrow={false}
                  disableInteractive={true}
                  enterDelay={500}
                  placement="bottom-start"
                  slotProps={{
                    popper: tooltipPopperProps,
                  }}
                >
                  <Typography
                    sx={getHeaderLabelTypoSx}
                    aria-label={`${inputLabel} - ${infoText}`}
                  >
                    {inputLabel}
                    {infoText ? <InfoOutlinedIcon sx={getInfoTextIconSx} /> : null}
                  </Typography>
                </Tooltip>
                {showRemove && (
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    disabled={disabled || valueIsEmpty}
                    startIcon={<RemoveCircleOutlineIcon />}
                    sx={getRemoveButtonSx}
                    onClick={(event) => handleClearSelection(event)}
                  >
                    {removeButtonText ?? t('blox.actions.remove')}
                  </Button>
                )}
              </Stack>

              {showFilter && (
                <TextField
                  data-testid={`select-filter-textfield--${normalizedInputId}`}
                  fullWidth={true}
                  data-private={isDataPrivate ? true : undefined}
                  autoComplete="off"
                  inputRef={filterInputRef}
                  value={filterText || ''}
                  disabled={disabled}
                  onChange={(event) => handleFilterChange(event)}
                  placeholder={filterPlaceholder}
                  variant="outlined"
                  size="small"
                  margin="dense"
                  sx={getFilterTextfieldSx}
                  onKeyDown={handleKeyDownFilterInput}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <Fade in={Boolean(filterText)}>
                          <Tooltip
                            title="Clear input"
                            disableInteractive
                          >
                            <InputAdornment position="end">
                              <ClearIcon
                                fontSize="small"
                                onClick={handleClearFilter}
                              />
                            </InputAdornment>
                          </Tooltip>
                        </Fade>
                      ),
                    },
                  }}
                />
              )}

              {showChipBox && (
                <Accordion
                  data-testid={`select-chipbox-accordion--${normalizedInputId}`}
                  disableGutters
                  elevation={0}
                  square
                  sx={getAccordionChipBoxSx}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={getExpandMoreIconSx} />}
                  >
                    <Typography
                      sx={{
                        fontSize: bloxValues.tinyFontSizeRem,
                      }}
                    >
                      {chipBoxText}
                    </Typography>
                    <Box sx={getDividerDecoratorSx} />
                  </AccordionSummary>
                  <AccordionDetails>
                    {chipBoxValueArr.map((valueStr, idx) => {
                      const choice = allChoicesDeduped.find(
                        (choice) => String(choice?.value) === String(valueStr),
                      );
                      const label = choice ? choiceTextString(choice) : valueStr;
                      return (
                        <Chip
                          key={`chip-${valueStr}-${idx}`}
                          label={label}
                          disabled={disabled}
                          onDelete={(event) => handleDeleteSelection(event, valueStr)}
                          size="small"
                          sx={getAccordionChipSx}
                          data-private={isDataPrivate ? true : undefined}
                        />
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>

            <Stack
              direction="column"
              sx={{
                gridArea: 'selectmainarea',
                overflow: 'auto',
                bgcolor: theme.palette.background.paper,
              }}
            >
              {loadingWithoutData ? (
                <SkeletonBox />
              ) : !hasLength(filteredUniqueChoices) ? (
                <SelectNoResults message={noResultsMessage} />
              ) : (
                <Fragment>
                  {error && (
                    <ErrorAlerts
                      severity="warning"
                      buttonOnClick={() => {
                        refetch();
                      }}
                      buttonText="Retry"
                      buttonIcon={<ReplayIcon />}
                      sx={{ m: 1 }}
                      errors={[
                        {
                          status: `Status:  ${(error as any)?.status || '\u2014'}`,
                          message: `Message: ${error?.message || '\u2014'}`,
                        },
                      ]}
                    />
                  )}
                  <Virtuoso
                    style={{ height: `${filteredUniqueChoices.length * 34}px` }}
                    increaseViewportBy={300}
                    data={
                      showDividerAfterStaticChoices
                        ? [
                            ...uniqueClientSideItems,
                            { value: '__divider__', label: '__divider__' },
                            ...flatApiChoices,
                          ]
                        : filteredUniqueChoices
                    }
                    itemContent={(index, choice) => {
                      if (choice?.value === '__divider__') {
                        return <Divider />;
                      }

                      const Component = renderMenuItem ?? SelectMenuItem;
                      return (
                        <Component
                          group={group}
                          disabled={disabled}
                          multiple={multiple}
                          isDataPrivate={isDataPrivate}
                          key={JSON.stringify(`${choice?.label}-${choice?.value}`)}
                          inputLabel={inputLabel}
                          choice={choice}
                          value={currentValues}
                          handleSelectionChange={handleSelectionChange}
                          intersectionRef={
                            index === filteredUniqueChoices.length - 1 ? ixRef : undefined
                          }
                          cycleSelectionState={cycleSelectionState}
                        />
                      );
                    }}
                  />
                </Fragment>
              )}
            </Stack>

            <Stack
              sx={{
                gridArea: 'selectfooter',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gridTemplateAreas: `"listinfo closebtn"`,
                alignItems: 'center',
                ...getFooterContainerSx(theme, hasFooterItems),
              }}
            >
              {showFooter && (
                <Typography
                  sx={{
                    gridArea: 'listinfo',
                    ...getFooterTypographySx(theme),
                  }}
                >
                  {isFetching && <CircularProgress size={bloxValues.smallFontSizeRem} />}
                  {showFilter &&
                    t('blox.general.results_of_total', {
                      results: countFilteredUniqueChoices,
                      count: totalResults,
                    })}
                </Typography>
              )}

              {showCloseButton && (
                <ButtonBase
                  onClick={handlePopoverClose}
                  sx={{
                    gridArea: 'closebtn',
                    ...getCloseButtonSx(theme),
                  }}
                  data-testid={`select-dropdown-close-button--${normalizedInputId}`}
                >
                  {t('blox.actions.close')}
                </ButtonBase>
              )}
            </Stack>
          </Popover>
        </FormControl>
      </Tooltip>
    );
  },
);
SelectDropdownInput.displayName = 'SelectDropdownInput';
